import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { BlogHeader, BlogFooter } from "../components/blog/BlogHeader";
import { PostCard } from "../components/blog/PostCard";
import { SignupCTA } from "../components/blog/SignupCTA";

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [category, setCategory] = useState("All");
  usePageMeta(
    "Job Search Guides — Trackr",
    "Free, practical guides on tracking job applications, follow-up emails, salary negotiation, interviews and resumes."
  );

  useEffect(() => {
    api.get("/blog/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  const categories = useMemo(() => ["All", ...new Set((posts || []).map((p) => p.category))], [posts]);
  const visible = (posts || []).filter((p) => category === "All" || p.category === category);
  const [featured, ...rest] = visible;

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10">
        <section className="pt-16 pb-12 max-w-3xl">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">The Trackr guides</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium mt-4" data-testid="blog-index-title">
            Smarter job searching, <span className="text-[#8B9D83]">one guide at a time.</span>
          </h1>
          <p className="text-base text-[#5C6B64] mt-6 leading-relaxed">
            Practical, no-fluff advice on tracking applications, following up, negotiating and landing the offer.
          </p>
        </section>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${category === c ? "bg-[#1A362D] text-white border-[#1A362D]" : "border-[#E5E1DB] bg-white hover:border-[#1A362D]"}`}
              data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {posts === null && <div className="h-64 rounded-xl bg-[#E5E1DB] animate-pulse" />}
        {posts && visible.length === 0 && <p className="text-[#5C6B64]" data-testid="blog-empty">No guides yet — check back soon.</p>}
        {featured && <PostCard post={featured} featured />}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8" data-testid="blog-post-grid">
          {rest.map((p, i) => (
            <motion.div key={p.slug} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <PostCard post={p} />
            </motion.div>
          ))}
        </div>
        <SignupCTA variant="end" />
      </main>
      <BlogFooter />
    </div>
  );
}
